/**
 * The /webinars surface (CR-24, upgraded by CR-27 into the DevHub-style webinar hub): the
 * Syncra webinar dashboard merging the sources of GET /api/v1/webinars into one view.
 *
 * Sections:
 *  - «Мои вебинары» (CR-27): the events the viewer is registered for, with their statuses -
 *    the attendee's shortlist. (The DevHub speaker view - events one ORGANIZES - is deferred:
 *    the wire carries no organizer identity, see the slice report.)
 *  - «Идёт сейчас»: live rooms with the audience count and a straight JOIN.
 *  - «Расписание»: upcoming events as informative cards - the badges row (registration state),
 *    the date, the two-line title, the two-line description, the seats left («Нет мест»
 *    highlighted when the roster is full) - each card opening the /webinars/<id> detail page;
 *    JOIN stays on the card (opens the room, a lobby note marks rooms where non-staff wait).
 *  - «Прошедшие»: last 30 days with the completed recordings count deep-linking into the
 *    filtered archive; event rows open the detail page too.
 *
 * Guests get the sign-in prompt; the SSO session, its auto-refresh and the 401-recovery
 * wrapper are the dashboard's shared machinery. The listing polls while the page is open:
 * live presence is the point of the surface.
 *
 * UX decision (recorded in the CR-24 slice report, kept): the JOIN button is always enabled -
 * it opens the room, and a lobby_required room holds non-staff in the lobby server-side.
 */

import { ArrowLeft, CalendarClock, CheckCircle2, Film, Radio, RefreshCw, Users, Video } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../components/ui/button';
import {
  listWebinars,
  splitWebinarSections,
  webinarDetailPath,
  webinarFull,
  webinarSeatsLeft,
  WebinarsApiError,
} from '../api/webinars';
import type { WebinarEntry } from '../api/webinars';
import {
  browserSsoStorage,
  callWithSsoRecovery,
  loadSsoSession,
  startSsoAutoRefresh,
} from '../auth/sso';
import { showToast, ToastStack } from '../calendar/Toast';
import {
  checkSession,
  forgetSession,
  startCalendarSignIn,
  calendarSsoConfig,
} from '../calendar/signIn';
import { navigateTo } from '../lib/spa';
import type { KeyValueStorage } from '../room/devicePrefs';
import { cn } from '../lib/utils';

/** Poll interval while the page is open: the live section must not go stale. */
const WEBINARS_POLL_MS = 30_000;

type LoadState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly entries: readonly WebinarEntry[] }
  | { readonly kind: 'error' };

export interface WebinarsPageProps {
  /** Injected for tests; defaults to the browser's shared SSO store (localStorage-backed). */
  readonly storage?: KeyValueStorage | null;
  /** Injected for tests; defaults to the wall clock. */
  readonly now?: Date;
  /** Fetch override for tests; defaults to the global fetch. */
  readonly fetchImpl?: typeof fetch;
  /** Poll interval override for tests; production keeps 30 s. */
  readonly pollMs?: number | undefined;
}

/** The SPA path of this page (the dashboard sidebar's «Вебинары» entry leads here). */
export const WEBINARS_PATH = '/webinars';

/** Stable React key of one catalog row: the event occurrence, or the ad-hoc room itself. */
export function webinarRowKey(entry: WebinarEntry): string {
  if (entry.event !== null) {
    return `e${String(entry.event.id)}-${entry.event.startsAt}`;
  }
  return `r-${entry.room.slug}`;
}

export function WebinarsPage({
  storage,
  now,
  fetchImpl,
  pollMs,
}: WebinarsPageProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'ru' ? 'ru-RU' : 'en-US';
  const store = useMemo(() => storage ?? browserSsoStorage(), [storage]);
  const at = useMemo(() => now ?? new Date(), [now]);
  const nowMs = useCallback((): number => (now !== undefined ? now.getTime() : Date.now()), [now]);
  const doFetch = fetchImpl ?? globalThis.fetch;

  const [session, setSession] = useState<'signed_in' | 'guest'>(() =>
    store !== null && checkSession(store, at) === 'signed_in' ? 'signed_in' : 'guest',
  );
  const [token, setToken] = useState<string | null>(() =>
    store !== null && checkSession(store, at) === 'signed_in'
      ? (loadSsoSession(store)?.accessToken ?? null)
      : null,
  );

  const [load, setLoad] = useState<LoadState>({ kind: 'loading' });
  const refreshSeqRef = useRef(0);
  const loadStartedRef = useRef(false);

  // A bearer the service rejected after the recovery wrapper: the rotation chain is dead.
  const handleUnauthorized = useCallback((): void => {
    if (store !== null) {
      forgetSession(store);
    }
    setToken(null);
    setSession('guest');
  }, [store]);

  // Auto-refresh keeps the bearer alive while the tab is open (same contract as the dashboard).
  useEffect(() => {
    if (store === null || session !== 'signed_in') {
      return undefined;
    }
    return startSsoAutoRefresh({
      config: calendarSsoConfig(),
      storage: store,
      fetchImpl: doFetch,
      nowMs,
      onRefreshed: (freshToken) => setToken(freshToken),
      onInvalidGrant: handleUnauthorized,
    });
  }, [store, doFetch, handleUnauthorized, nowMs, session]);

  const refresh = useCallback((): void => {
    const currentToken = token;
    if (currentToken === null) {
      return;
    }
    const seq = refreshSeqRef.current + 1;
    refreshSeqRef.current = seq;
    callWithSsoRecovery(currentToken, (fresh) => listWebinars(fresh, doFetch), {
      config: calendarSsoConfig(),
      storage: store,
      fetchImpl: doFetch,
      rejected: (err: unknown): boolean => err instanceof WebinarsApiError && err.status === 401,
      onRefreshed: (freshToken) => setToken(freshToken),
      nowMs,
    })
      .then((entries) => {
        if (refreshSeqRef.current === seq) {
          setLoad({ kind: 'ready', entries });
        }
      })
      .catch((err: unknown) => {
        if (refreshSeqRef.current !== seq) {
          return;
        }
        if (err instanceof WebinarsApiError && err.status === 401) {
          handleUnauthorized();
          return;
        }
        showToast('error', t('webinars.list.error'));
        setLoad({ kind: 'error' });
      });
  }, [token, doFetch, store, nowMs, handleUnauthorized, t]);

  // One load per signed-in mount (StrictMode double-mounts guarded by the ref).
  useEffect(() => {
    if (session !== 'signed_in' || loadStartedRef.current) {
      return;
    }
    loadStartedRef.current = true;
    refresh();
  }, [session, refresh]);

  // Poll while the page is open - live presence is the point of the surface.
  useEffect(() => {
    if (session !== 'signed_in' || token === null) {
      return undefined;
    }
    const timer = window.setInterval(() => {
      refresh();
    }, pollMs ?? WEBINARS_POLL_MS);
    return () => {
      window.clearInterval(timer);
    };
  }, [session, token, pollMs, refresh]);

  const handleSignIn = useCallback(async (): Promise<void> => {
    if (store === null) {
      showToast('error', t('sso.notice_storage_blocked'));
      return;
    }
    try {
      await startCalendarSignIn(
        calendarSsoConfig(),
        window.location.origin,
        store,
        window.location,
        WEBINARS_PATH,
      );
    } catch {
      // staying on the page; the button retries
    }
  }, [store, t]);

  const sections = useMemo(
    () => (load.kind === 'ready' ? splitWebinarSections(load.entries, at) : null),
    [load, at],
  );

  // «Мои вебинары» (CR-27): the rows the viewer is registered for, in catalog order (the
  // server's live-first sort already carries the urgency).
  const mine = useMemo(() => {
    if (load.kind !== 'ready') {
      return [];
    }
    return load.entries.filter((entry) => entry.registration?.me === 'registered');
  }, [load]);

  const title = (entry: WebinarEntry): string =>
    entry.event !== null ? entry.event.title : t('webinars.list.adhoc_title');

  const openDetail = useCallback((entry: WebinarEntry): void => {
    if (entry.event !== null) {
      navigateTo(webinarDetailPath(entry.event.id));
    }
  }, []);

  return (
    <div
      data-testid="webinars-page"
      className="flex h-screen flex-col overflow-hidden bg-background text-foreground"
    >
      <header className="flex h-14 shrink-0 items-center gap-3 border-b border-border bg-card px-4 sm:px-6">
        <Button
          variant="ghost"
          size="icon"
          className="size-9 text-muted-foreground hover:text-foreground"
          aria-label={t('webinars.back_aria')}
          data-testid="webinars-back"
          onClick={() => navigateTo('/')}
        >
          <ArrowLeft aria-hidden className="size-4" />
        </Button>
        <h1 className="truncate text-[15px] font-semibold tracking-tight text-foreground">
          {t('webinars.page_title')}
        </h1>
        {sections !== null ? (
          <span
            data-testid="webinars-count"
            className="ml-1 rounded-full border border-border px-2 py-0.5 text-[10.5px] font-semibold tabular-nums text-muted-foreground"
          >
            {String(sections.live.length + sections.upcoming.length + sections.past.length)}
          </span>
        ) : null}
        <button
          type="button"
          data-testid="webinars-refresh"
          aria-label={t('webinars.refresh_aria')}
          className="ml-auto grid size-8 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
          disabled={session !== 'signed_in'}
          onClick={() => {
            refresh();
          }}
        >
          <RefreshCw aria-hidden className="size-4" />
        </button>
      </header>

      <div className="scroll-thin min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-[920px] px-4 py-6 sm:px-6 lg:px-8">
          {session === 'guest' ? (
            <div
              className="flex flex-col items-start gap-3 rounded-xl border border-border bg-card p-5 sm:flex-row sm:items-center sm:justify-between"
              data-testid="webinars-gate"
            >
              <div className="min-w-0">
                <p className="text-[14px] font-semibold text-foreground">
                  {t('webinars.list.gate.title')}
                </p>
                <p className="mt-1 text-[12.5px] leading-relaxed text-muted-foreground">
                  {t('webinars.list.gate.description')}
                </p>
              </div>
              <button
                type="button"
                data-testid="webinars-gate-signin"
                className="shrink-0 rounded-lg bg-primary px-4 py-2 text-[13px] font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
                onClick={() => {
                  void handleSignIn();
                }}
              >
                {t('calendar.gate.sign_in')}
              </button>
            </div>
          ) : load.kind === 'loading' ? (
            <div
              className="flex items-center gap-2 rounded-xl border border-border bg-card p-4 text-[13px] text-muted-foreground"
              data-testid="webinars-loading"
            >
              {t('common.loading')}
            </div>
          ) : load.kind === 'error' ? (
            <div
              role="alert"
              className="rounded-xl border border-border bg-card p-4 text-[13px] text-muted-foreground"
              data-testid="webinars-error"
            >
              {t('webinars.list.error')}
            </div>
          ) : sections === null ? (
            <div data-testid="webinars-loading">{t('common.loading')}</div>
          ) : (
            <>
              {/* Мои вебинары (CR-27): the viewer's registrations, statuses attached */}
              {mine.length > 0 ? (
                <section aria-label={t('webinars.my.title')} className="scroll-mt-20">
                  <SectionLabel>{t('webinars.my.title')}</SectionLabel>
                  <div className="overflow-hidden rounded-xl border border-emerald-500/30 bg-card">
                    {mine.map((entry) => (
                      <div
                        key={webinarRowKey(entry)}
                        data-testid={`webinar-mine-row-${webinarRowKey(entry)}`}
                        className="flex min-w-0 items-center gap-3 border-b border-border px-4 py-3 transition-colors last:border-b-0 hover:bg-accent/50"
                      >
                        <CheckCircle2
                          aria-hidden
                          className="size-4 shrink-0 text-emerald-400"
                        />
                        <div className="min-w-0 flex-1">
                          {entry.event !== null ? (
                            <button
                              type="button"
                              data-testid={`webinar-mine-link-${webinarRowKey(entry)}`}
                              className="block max-w-full truncate text-left text-[14px] font-medium text-foreground hover:underline"
                              onClick={() => {
                                openDetail(entry);
                              }}
                            >
                              {title(entry)}
                            </button>
                          ) : (
                            <p className="truncate text-[14px] font-medium text-foreground">
                              {title(entry)}
                            </p>
                          )}
                          <p className="mt-0.5 flex min-w-0 items-center gap-2 text-[11.5px] text-muted-foreground">
                            {entry.event !== null ? (
                              <span className="shrink-0 tabular-nums">
                                {new Intl.DateTimeFormat(locale, {
                                  day: 'numeric',
                                  month: 'short',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                }).format(new Date(entry.event.startsAt))}
                              </span>
                            ) : null}
                            <span className="truncate font-mono">/r/{entry.room.slug}</span>
                          </p>
                        </div>
                        {entry.live !== null ? (
                          <span className="shrink-0 rounded-md bg-emerald-500/10 px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider text-emerald-400">
                            {t('webinars.list.live_badge')}
                          </span>
                        ) : (
                          <SeatsChip entry={entry} testId={`webinar-mine-seats-${webinarRowKey(entry)}`} />
                        )}
                        <JoinButton
                          slug={entry.room.slug}
                          testId={`webinar-mine-join-${webinarRowKey(entry)}`}
                          className="shrink-0"
                        />
                      </div>
                    ))}
                  </div>
                </section>
              ) : null}

              {/* Идёт сейчас */}
              <section
                aria-label={t('webinars.list.live')}
                className={cn(mine.length > 0 ? 'mt-8' : '', 'scroll-mt-20')}
              >
                <SectionLabel>{t('webinars.list.live')}</SectionLabel>
                {sections.live.length === 0 ? (
                  <EmptyCard
                    testId="webinars-live-empty"
                    title={t('webinars.list.empty_live')}
                    hint={t('webinars.list.empty_live_hint')}
                  />
                ) : (
                  <div className="grid gap-3 sm:grid-cols-2">
                    {sections.live.map((entry) => (
                      <article
                        key={webinarRowKey(entry)}
                        data-testid={`webinar-live-card-${entry.room.slug}`}
                        className="relative overflow-hidden rounded-xl border border-emerald-500/40 bg-card p-4"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span
                            data-testid={`webinar-live-badge-${entry.room.slug}`}
                            className="inline-flex items-center gap-1.5 rounded-md bg-emerald-500/10 px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider text-emerald-400"
                          >
                            <span
                              aria-hidden
                              className="size-1.5 animate-pulse rounded-full bg-emerald-500"
                            />
                            {t('webinars.list.live_badge')}
                          </span>
                          <span
                            data-testid={`webinar-live-audience-${entry.room.slug}`}
                            className="inline-flex items-center gap-1 text-[11.5px] font-medium tabular-nums text-muted-foreground"
                          >
                            <Users aria-hidden className="size-3.5" />
                            {t('webinars.list.audience', { count: entry.live?.participants ?? 0 })}
                          </span>
                        </div>
                        <button
                          type="button"
                          data-testid={`webinar-live-title-${entry.room.slug}`}
                          className="mt-2.5 block max-w-full truncate text-left text-[14.5px] font-semibold text-foreground hover:underline"
                          onClick={() => {
                            openDetail(entry);
                          }}
                        >
                          {title(entry)}
                        </button>
                        <p className="mt-0.5 truncate font-mono text-[11.5px] text-muted-foreground">
                          /r/{entry.room.slug}
                        </p>
                        {entry.room.lobby ? <LobbyNote /> : null}
                        <JoinButton
                          slug={entry.room.slug}
                          testId={`webinar-live-join-${entry.room.slug}`}
                          className="mt-3 w-full"
                        />
                      </article>
                    ))}
                  </div>
                )}
              </section>

              {/* Расписание */}
              <section aria-label={t('webinars.list.schedule')} className="mt-8">
                <SectionLabel>{t('webinars.list.schedule')}</SectionLabel>
                {sections.upcoming.length === 0 ? (
                  <EmptyCard
                    testId="webinars-schedule-empty"
                    title={t('webinars.list.empty_schedule')}
                    hint={t('webinars.list.empty_schedule_hint')}
                  />
                ) : (
                  <div className="grid gap-3 sm:grid-cols-2">
                    {sections.upcoming.map((entry) => (
                      <ScheduleCard
                        key={webinarRowKey(entry)}
                        entry={entry}
                        locale={locale}
                        title={title(entry)}
                        onOpen={() => {
                          openDetail(entry);
                        }}
                      />
                    ))}
                  </div>
                )}
              </section>

              {/* Прошедшие */}
              <section aria-label={t('webinars.list.past')} className="mt-8">
                <SectionLabel>{t('webinars.list.past')}</SectionLabel>
                {sections.past.length === 0 ? (
                  <EmptyCard
                    testId="webinars-past-empty"
                    title={t('webinars.list.empty_past')}
                    hint={t('webinars.list.empty_past_hint')}
                  />
                ) : (
                  <div className="overflow-hidden rounded-xl border border-border bg-card">
                    {sections.past.map((entry) => (
                      <div
                        key={webinarRowKey(entry)}
                        data-testid={`webinar-past-row-${webinarRowKey(entry)}`}
                        role={entry.event !== null ? 'button' : undefined}
                        tabIndex={entry.event !== null ? 0 : undefined}
                        onKeyDown={(event) => {
                          if (entry.event !== null && (event.key === 'Enter' || event.key === ' ')) {
                            event.preventDefault();
                            openDetail(entry);
                          }
                        }}
                        onClick={() => {
                          openDetail(entry);
                        }}
                        className={cn(
                          'flex min-w-0 items-center gap-3 border-b border-border px-4 py-3.5 transition-colors last:border-b-0 hover:bg-accent/50',
                          entry.event !== null ? 'cursor-pointer' : '',
                        )}
                      >
                        {/* An ad-hoc past row carries no date on the wire - the dash marks it. */}
                        <div className="w-[104px] shrink-0 text-left text-[12.5px] tabular-nums text-muted-foreground">
                          {entry.event === null
                            ? '—'
                            : new Intl.DateTimeFormat(locale, {
                                day: 'numeric',
                                month: 'short',
                              }).format(new Date(entry.event.startsAt))}
                        </div>
                        <div aria-hidden className="h-9 w-px shrink-0 bg-border" />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-[14px] font-medium text-foreground">
                            {title(entry)}
                          </p>
                          <p className="mt-0.5 truncate font-mono text-[11.5px] text-muted-foreground">
                            /r/{entry.room.slug}
                          </p>
                        </div>
                        {entry.recordings !== null && entry.recordings.count > 0 ? (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 shrink-0 gap-1.5 px-3 text-[12.5px] font-semibold"
                            data-testid={`webinar-past-recordings-${webinarRowKey(entry)}`}
                            aria-label={t('webinars.list.recordings_aria', { room: entry.room.slug })}
                            onClick={(event) => {
                              // The chip deep-links into the archive; it must not also fire
                              // the row's detail navigation.
                              event.stopPropagation();
                              navigateTo(`/recordings?room=${entry.room.slug}`);
                            }}
                          >
                            <Film aria-hidden className="size-3.5" />
                            {t('webinars.list.recordings_count', {
                              count: entry.recordings.count,
                            })}
                          </Button>
                        ) : null}
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </>
          )}
        </div>
      </div>

      <ToastStack />
    </div>
  );
}

/**
 * The schedule card (CR-27, the DevHub-style informative card): the badges row - the
 * registration badge («Регистрация» when seats remain, «Нет мест» highlighted when the
 * roster is full), the date and time, the two-line title, the two-line description and the
 * seats line; the whole card opens the detail page, the JOIN button stays on the card.
 */
function ScheduleCard(props: {
  readonly entry: WebinarEntry;
  readonly locale: string;
  readonly title: string;
  readonly onOpen: () => void;
}): JSX.Element {
  const { entry, locale, title, onOpen } = props;
  const { t } = useTranslation();
  const full = webinarFull(entry);
  const key = webinarRowKey(entry);
  return (
    <article
      data-testid={`webinar-schedule-row-${key}`}
      role="button"
      tabIndex={0}
      aria-label={title}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen();
        }
      }}
      onClick={onOpen}
      className="flex cursor-pointer flex-col rounded-xl border border-border bg-card p-4 transition-colors hover:border-muted-foreground/40"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 flex-wrap items-center gap-1.5">
          {full ? (
            <span
              data-testid={`webinar-full-badge-${key}`}
              className="rounded-md bg-red-500/10 px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider text-red-400"
            >
              {t('webinars.list.full_badge')}
            </span>
          ) : entry.registration !== null ? (
            <span
              data-testid={`webinar-registration-badge-${key}`}
              className="inline-flex items-center gap-1 rounded-md bg-primary/10 px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider text-primary"
            >
              <CalendarClock aria-hidden className="size-3" />
              {t('webinars.list.registration_badge')}
            </span>
          ) : null}
        </div>
        <div className="flex shrink-0 flex-col items-end">
          <span className="text-[12.5px] font-semibold tabular-nums text-foreground">
            {entry.event === null
              ? null
              : new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' }).format(
                  new Date(entry.event.startsAt),
                )}
          </span>
          <span className="text-[11px] tabular-nums text-muted-foreground">
            {entry.event === null
              ? null
              : new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(
                  new Date(entry.event.startsAt),
                )}
          </span>
        </div>
      </div>
      <p className="mt-2.5 line-clamp-2 text-[14px] font-semibold leading-snug text-foreground">
        {title}
      </p>
      {entry.event?.description !== null && entry.event?.description !== undefined && entry.event.description.length > 0 ? (
        <p className="mt-1 line-clamp-2 text-[12px] leading-relaxed text-muted-foreground">
          {entry.event.description}
        </p>
      ) : null}
      <p className="mt-2 truncate font-mono text-[11px] text-muted-foreground">/r/{entry.room.slug}</p>
      <div className="mt-3 flex items-center justify-between gap-2">
        <SeatsChip
          entry={entry}
          testId={`webinar-seats-${key}`}
        />
        {entry.room.lobby ? <LobbyNote inline /> : null}
        <JoinButton
          slug={entry.room.slug}
          testId={`webinar-schedule-join-${key}`}
          className="shrink-0"
        />
      </div>
    </article>
  );
}

/**
 * The seats line (CR-27): «N мест» left when the event caps its roster and seats remain,
 * «Нет мест» highlighted when the roster is full, nothing when the cap is unlimited or the
 * row carries no registration section.
 */
function SeatsChip(props: { readonly entry: WebinarEntry; readonly testId: string }): JSX.Element | null {
  const { entry, testId } = props;
  const { t } = useTranslation();
  const seats = webinarSeatsLeft(entry);
  if (seats === null) {
    return null;
  }
  if (seats === 0) {
    return (
      <span
        data-testid={testId}
        className="rounded-md bg-red-500/10 px-2 py-0.5 text-[11px] font-semibold text-red-400"
      >
        {t('webinars.list.no_seats')}
      </span>
    );
  }
  return (
    <span
      data-testid={testId}
      className="rounded-md bg-muted px-2 py-0.5 text-[11px] font-medium tabular-nums text-muted-foreground"
    >
      {t('webinars.list.seats_left', { count: seats })}
    </span>
  );
}

function SectionLabel({ children }: { children: JSX.Element | string }): JSX.Element {
  return (
    <h2 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
      {children}
    </h2>
  );
}

function EmptyCard(props: { readonly testId: string; readonly title: string; readonly hint: string }): JSX.Element {
  return (
    <div
      className="rounded-xl border border-border bg-card p-4"
      data-testid={props.testId}
    >
      <p className="text-[13px] font-medium text-foreground">{props.title}</p>
      <p className="mt-0.5 text-[12px] text-muted-foreground">{props.hint}</p>
    </div>
  );
}

/** The lobby expectation note: joining opens the room, the lobby holds non-staff. */
function LobbyNote({ inline = false }: { readonly inline?: boolean }): JSX.Element {
  const { t } = useTranslation();
  return (
    <span
      data-testid="webinar-lobby-note"
      className={cn(
        'inline-flex min-w-0 items-center gap-1 rounded border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-medium text-amber-300',
        inline ? '' : 'mt-2',
      )}
    >
      <Radio aria-hidden className="size-3 shrink-0" />
      {t('webinars.list.lobby_note')}
    </span>
  );
}

function JoinButton(props: {
  readonly slug: string;
  readonly testId: string;
  readonly className?: string | undefined;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <Button
      size="sm"
      className={cn('gap-1.5 px-3 text-[12.5px] font-semibold', props.className)}
      data-testid={props.testId}
      onClick={(event) => {
        // JOIN opens the room; on the clickable cards it must not fire the detail navigation.
        event.stopPropagation();
        navigateTo(`/r/${props.slug}`);
      }}
    >
      <Video aria-hidden className="size-3.5" />
      {t('webinars.list.join')}
    </Button>
  );
}
